import React from "react";
import { Outlet, useMatch, useNavigate, useParams } from "react-router-dom";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import { useIsMobile } from "@app.automotus.io/components/hooks/useIsMobile";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import { TransactionHeader } from "@app.automotus.io/components/TransactionHeader";
import Stack from "@mui/material/Stack";
import ButtonBase, { ButtonBaseProps } from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";

export const TransactionLayout: React.FC<TransactionLayoutProps> = ({
  stackViolationLabel = false,
  fullWidth = false,
}) => {
  const { transactionId = "" } = useParams<{ transactionId: string }>();
  const matchDispute = useMatch("/transactions/:transactionId/dispute");

  const content = (
    <Stack spacing={3}>
      <TransactionHeader transactionId={transactionId} stackViolationLabel={stackViolationLabel} />
      <Outlet />
    </Stack>
  );

  return (
    <Container maxWidth={"lg"}>
      <BackButton sx={{ mb: { xs: 3, lg: 6 }, mt: { xs: 3, lg: 5 } }} title={matchDispute ? "Summary" : ""} />
      {fullWidth ? (
        <Box sx={{ pb: { xs: 6.25, lg: 17.25 } }}>
          <Box>{content}</Box>
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" sx={{ pb: { xs: 6.25, lg: 17.25 } }}>
          <Box sx={{ maxWidth: 720 }}>{content}</Box>
        </Box>
      )}
    </Container>
  );
};

export interface TransactionLayoutProps {
  /** Stacks the Violation badge onto its own line below the title instead of inline, at every screen size. */
  stackViolationLabel?: boolean;
  /** Renders content at the full `lg` Container width instead of centering it in a 720px column. */
  fullWidth?: boolean;
}

const BackButton: React.FC<ButtonBaseProps> = ({ sx, title, ...props }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  return (
    <ButtonBase
      {...props}
      disableRipple={true}
      onClick={() => (title === "Summary" ? navigate(".", { replace: true }) : navigate(-1))}
      sx={{ ...sx, "& :hover": { color: (theme: Theme) => theme.palette.action.active } }}
    >
      <Box display={"flex"} alignItems={"center"} sx={{ p: 1 }}>
        <ArrowBackIos />
        {!isMobile && (
          <Typography variant={"h7"} sx={{ ml: 0.5 }}>
            Back {title && `to ${title}`}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
};
